import React, { useMemo, useState } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormLabel,
	MenuItem,
	Select,
	Grid,
	TextField,
	Typography,
	FormHelperText,
	Tooltip,
	IconButton,
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { closeDialog } from '@/features/dialog/dialogSlice';
import { Form, Formik } from 'formik';
import { showToast, hideToast } from '@/features/toast/toastSlice';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { OwnerAutocomplete, OwnerSelect } from '@/components/selects/OwnerSelect';
import FileUpload from '@/components/FileUpload';
import { useCreateSupportTicketMutation } from '@/features/support/supportApi';
import {
	CATEGORY_OPTIONS,
	AREA_IMPACTED_OPTIONS,
	DESCRIPTION_MAX_LENGTH,
	supportTicketValidationSchema,
} from './validationSchema';
import { uniqueId } from 'lodash';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '@/utils/firebase';
import { v4 as uuidv4 } from 'uuid';
import { STORAGE_PURPOSES, userStoragePath } from '@/utils/storagePaths';

const getErrorMessage = (error, fallback) => {
	return error?.data?.message ?? error?.error ?? error?.message ?? fallback;
};

const ISSUE_REPORTING_DIALOG = 'issueReporting';

const defaultFormValues = {
	subject: '',
	category: '',
	areaImpacted: '',
	userAffected: null,
	description: '',
	attachments: [],
};

export const IssueReportingDialog = () => {
	const dispatch = useDispatch();
	const open = useSelector((state) => state.dialog.open?.issueReporting);
	const prefillData = useSelector((state) => state.dialog.data?.issueReporting ?? {});
	const formInitialValues = useMemo(
		() => ({ ...defaultFormValues, ...prefillData }),
		[prefillData],
	);

	const handleClose = () => {
		dispatch(closeDialog(ISSUE_REPORTING_DIALOG));
	};
	const { username: email } = useSelector((state) => state.msalAccount.account);

	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });

	const [createSupportTicket] = useCreateSupportTicketMutation();
	const [isSubmitting, setIsSubmitting] = useState(false);

	const uploadAttachmentsToStorage = async (files, userId) => {
		console.log('🔍 Starting attachment upload...');
		console.log('📎 Files count:', files?.length);
		console.log('👤 User ID:', userId);

		if (!files?.length) {
			console.log('ℹ️ No files to upload');
			return [];
		}

		// Unique across sessions, so a later ticket can't overwrite an earlier one's files.
		const submissionId = uuidv4();
		console.log('🆔 Submission ID:', submissionId);

		const uploadedFiles = await Promise.all(
			files.map(async (file, index) => {
				console.log(`\n--- File ${index + 1}/${files.length} ---`);

				try {
					// If file already has downloadUrl (already uploaded)
					if (file.downloadUrl || file.webUrl) {
						console.log('📎 Already uploaded file:', file.originalName || file.name);
						return {
							filename: file.originalName || file.name,
							downloadUrl: file.downloadUrl || file.webUrl,
							path: file.path || file.id,
						};
					}

					// Upload local file to Firebase
					if (file instanceof File) {
						console.log('📄 Uploading local file:', file.name);
						console.log('📋 Size:', (file.size / 1024).toFixed(2), 'KB');

						const filePath = userStoragePath(
							userId,
							STORAGE_PURPOSES.supportTickets,
							submissionId,
							file.name,
						);
						console.log('📁 Path:', filePath);

						const storageRef = ref(storage, filePath);
						const metadata = file.type ? { contentType: file.type } : undefined;

						// Upload to Firebase
						await uploadBytes(storageRef, file, metadata);
						console.log('✅ Uploaded to Firebase');

						// Get download URL
						const downloadUrl = await getDownloadURL(storageRef);
						console.log('🔗 Download URL:', downloadUrl);

						return {
							filename: file.name,
							downloadUrl: downloadUrl,
							path: filePath,
						};
					}

					console.warn('⚠️ Unknown file type:', file);
					return null;
				} catch (error) {
					console.error(`❌ Failed to upload file ${index + 1}:`, {
						fileName: file.name || 'unknown',
						error: error.message,
					});
					return null;
				}
			}),
		);

		const validUploads = uploadedFiles.filter(Boolean);
		console.log(`\n📊 Uploaded ${validUploads.length}/${files.length} files`);
		console.log('📎 Final attachments:', validUploads);

		if (validUploads.length === 0 && files.length > 0) {
			throw new Error('All files failed to upload. Please try again.');
		}

		return validUploads;
	};

	const handleSubmit = async (values, { resetForm }) => {
		console.log('🚀 Starting support ticket submission...');
		console.log('📝 Form values:', {
			subject: values.subject,
			category: values.category,
			areaImpacted: values.areaImpacted,
			description: values.description?.substring(0, 50) + '...',
			userAffected: values.userAffected,
			attachmentsCount: values.attachments.length,
		});

		// Log raw attachments
		console.log(
			'📎 Raw attachments:',
			values.attachments.map((f) => ({
				isFile: f instanceof File,
				hasDownloadUrl: !!f.downloadUrl,
				hasWebUrl: !!f.webUrl,
				name: f.name,
				size: f.size,
			})),
		);

		const toastId = uniqueId();
		setIsSubmitting(true);

		dispatch(
			showToast({
				id: toastId,
				message: 'Submitting support request...',
				autohide: false,
				loading: true,
			}),
		);

		try {
			let attachments = [];
			if (values.attachments.length > 0) {
				if (!userDetails?.userId) {
					throw new Error('User details not loaded. Please try again.');
				}

				// Update toast for file upload phase
				dispatch(hideToast(toastId));
				dispatch(
					showToast({
						id: toastId,
						message: `Uploading ${values.attachments.length} attachment(s)...`,
						autohide: false,
						loading: true,
					}),
				);

				attachments = await uploadAttachmentsToStorage(
					values.attachments,
					userDetails.userId,
				);

				console.log('✅ All attachments processed:', attachments);

				// Verify attachments have required fields
				attachments.forEach((att, i) => {
					console.log(`Attachment ${i + 1}:`, {
						filename: att.filename,
						downloadUrl: att.downloadUrl?.substring(0, 50) + '...',
						path: att.path,
					});
				});

				// Update toast for ticket creation
				dispatch(hideToast(toastId));
				dispatch(
					showToast({
						id: toastId,
						message: 'Creating support ticket...',
						autohide: false,
						loading: true,
					}),
				);
			}

			const payload = {
				subject: values.subject,
				category: values.category,
				areaImpacted: values.areaImpacted,
				description: values.description,
				userAffected: values.userAffected,
				attachments,
			};

			console.log('📤 Full payload to API:', JSON.stringify(payload, null, 2));

			const result = await createSupportTicket(payload).unwrap();

			console.log('✅ API Response:', result);

			const attachmentsProcessed = result?.attachmentsProcessed ?? 0;
			const attachmentResults = result?.attachmentResults ?? [];
			const failedAttachments = attachmentResults.filter((r) => !r.success);

			if (attachments.length > 0) {
				console.log('📎 Attachment results:', {
					requested: attachments.length,
					processed: attachmentsProcessed,
					results: attachmentResults,
				});
				if (failedAttachments.length > 0) {
					console.warn('⚠️ Some attachments failed to save in HubSpot:', failedAttachments);
				}
			}

			dispatch(hideToast(toastId));

			const attachmentNote =
				attachments.length > 0
					? failedAttachments.length > 0
						? ` (${attachmentsProcessed}/${attachments.length} attachments saved)`
						: ` (${attachmentsProcessed} attachment${attachmentsProcessed === 1 ? '' : 's'} saved)`
					: '';

			dispatch(
				showToast({
					message: `Support request submitted. Ticket ID: ${result.ticketId}${attachmentNote}`,
					success: failedAttachments.length === 0,
					autohide: true,
				}),
			);

			resetForm();
			handleClose();
		} catch (error) {
			console.error('❌ Submission failed:', {
				message: error.message,
				data: error.data,
				status: error.status,
			});

			dispatch(hideToast(toastId));

			const errorMessage = getErrorMessage(error, 'Could not submit support request.');

			dispatch(
				showToast({
					message: errorMessage,
					severity: 'error',
					autohide: true,
				}),
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Formik
			initialValues={formInitialValues}
			validateOnChange={false}
			validateOnBlur={true}
			validationSchema={supportTicketValidationSchema}
			enableReinitialize={true}
			onSubmit={handleSubmit}
		>
			{({
				setFieldValue,
				values,
				handleSubmit,
				handleChange,
				touched,
				errors,
				getFieldProps,
				resetForm,
			}) => {
				return (
					<Form>
						<Dialog
							open={open}
							onClose={() => {
								resetForm();
								handleClose();
							}}
							fullWidth
							maxWidth='md'
							PaperProps={{ sx: { borderRadius: 2 } }}
						>
							<DialogTitle sx={{ pb: 0.5 }}>
								<Typography variant='h6' component='span' fontWeight='bold'>
									Raise an issue
								</Typography>
								<Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
									Complete below to raise a support ticket or feature request
								</Typography>
							</DialogTitle>

							<DialogContent sx={{ p: 3 }}>
								<Grid
									container
									spacing={2}
									sx={{
										'& .MuiFormLabel-root': { minHeight: '24px' },
									}}
								>
									<Grid size={12}>
										<FormControl
											required
											fullWidth
											error={touched.subject && Boolean(errors.subject)}
											sx={{ '& .MuiInputBase-root': { height: 40 } }}
										>
											<FormLabel required>Subject</FormLabel>
											<TextField
												fullWidth
												placeholder='Brief summary of the issue or request'
												{...getFieldProps('subject')}
												error={touched.subject && Boolean(errors.subject)}
												disabled={isSubmitting}
												size='small'
											/>
											{touched.subject && errors.subject && (
												<FormHelperText error>
													{errors.subject}
												</FormHelperText>
											)}
										</FormControl>
									</Grid>

									<Grid size={{ xs: 12, sm: 6 }}>
										<FormControl
											required
											fullWidth
											error={touched.category && Boolean(errors.category)}
											sx={{ '& .MuiInputBase-root': { height: 40 } }}
										>
											<FormLabel required>
												Category
												<Tooltip
													title='Select the appropriate category for your issue or request'
													placement='top'
												>
													<IconButton size='small' sx={{ ml: 0.25 }}>
														<InfoIcon sx={{ fontSize: '14px' }} />
													</IconButton>
												</Tooltip>
											</FormLabel>
											<Select
												{...getFieldProps('category')}
												displayEmpty
												disabled={isSubmitting}
											>
												<MenuItem value='' disabled>
													Select a category
												</MenuItem>
												{CATEGORY_OPTIONS.map(
													({ value, label, tooltip }) => (
														<MenuItem key={value} value={value}>
															<Box
																sx={{
																	display: 'flex',
																	alignItems: 'center',
																	justifyContent: 'space-between',
																	width: '100%',
																}}
															>
																<span>{label}</span>
																<Tooltip
																	title={tooltip}
																	placement='top'
																>
																	<IconButton
																		size='small'
																		sx={{ ml: 1 }}
																	>
																		<InfoIcon fontSize='small' />
																	</IconButton>
																</Tooltip>
															</Box>
														</MenuItem>
													),
												)}
											</Select>
											{touched.category && errors.category && (
												<FormHelperText error>
													{errors.category}
												</FormHelperText>
											)}
										</FormControl>
									</Grid>

									<Grid size={{ xs: 12, sm: 6 }}>
										<FormControl
											required
											fullWidth
											error={
												touched.areaImpacted && Boolean(errors.areaImpacted)
											}
											sx={{ '& .MuiInputBase-root': { height: 40 } }}
										>
											<FormLabel required>Area impacted</FormLabel>
											<Select
												{...getFieldProps('areaImpacted')}
												displayEmpty
												disabled={isSubmitting}
											>
												<MenuItem value='' disabled>
													Select an area
												</MenuItem>
												{AREA_IMPACTED_OPTIONS.map(({ value, label }) => (
													<MenuItem key={value} value={value}>
														{label}
													</MenuItem>
												))}
											</Select>
											{touched.areaImpacted && errors.areaImpacted && (
												<FormHelperText error>
													{errors.areaImpacted}
												</FormHelperText>
											)}
										</FormControl>
									</Grid>

									<Grid size={{ xs: 12, sm: 6 }}>
										<FormControl fullWidth sx={{ '& .MuiInputBase-root': { height: 40 } }}>
											<FormLabel>Reporter</FormLabel>
											<OwnerAutocomplete
												userId={userDetails?.userId}
												disabled={true}
											/>
										</FormControl>
									</Grid>

									<Grid size={{ xs: 12, sm: 6 }}>
										<FormControl fullWidth sx={{ '& .MuiInputBase-root': { height: 40 } }}>
											<FormLabel>User affected</FormLabel>
											<OwnerAutocomplete
												userId={values.userAffected}
												onChange={(user) => {
													setFieldValue('userAffected', user?.id ?? null);
												}}
												filterBy={(user) => user?.email != null}
												disabled={isSubmitting}
											/>
										</FormControl>
									</Grid>

									<Grid size={12}>
										<FormControl
											required
											fullWidth
											error={
												touched.description && Boolean(errors.description)
											}
										>
											<FormLabel required>Description</FormLabel>
											<TextField
												fullWidth
												multiline
												rows={6}
												placeholder='Please provide detailed information about the issue or request. Include steps to reproduce if applicable.'
												{...getFieldProps('description')}
												error={
													touched.description &&
													Boolean(errors.description)
												}
												helperText={
													(touched.description && errors.description) ||
													`${values.description.length}/${DESCRIPTION_MAX_LENGTH} characters`
												}
												disabled={isSubmitting}
											/>
										</FormControl>
									</Grid>

									<Grid size={12}>
										<FileUpload
											files={values.attachments}
											setFiles={(files) => {
												handleChange({
													target: {
														name: 'attachments',
														value: files,
													},
												});
											}}
											loading={isSubmitting}
										/>
									</Grid>
								</Grid>
							</DialogContent>

							<DialogActions>
								<Button
									onClick={() => {
										resetForm();
										handleClose();
									}}
									color='primary'
								>
									Cancel
								</Button>
								<Button
									color='primary'
									variant='contained'
									disabled={isSubmitting}
									onClick={() => {
										// Check if any validation errors exist
										if (Object.keys(errors).length > 0) {
											// Trigger validation by touching all fields
											setFieldValue('subject', values.subject);
											setFieldValue('category', values.category);
											setFieldValue('areaImpacted', values.areaImpacted);
											setFieldValue('description', values.description);
											return;
										}

										handleSubmit();
									}}
								>
									{isSubmitting ? (
										<CircularProgress size='2em' sx={{ color: '#ffffff' }} />
									) : (
										'Confirm'
									)}
								</Button>
							</DialogActions>
						</Dialog>
					</Form>
				);
			}}
		</Formik>
	);
};
